import {
  Component,
  effect,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { getState } from '@ngrx/signals';
import { GeoDataStore } from '../../../stores/geo/geo-data.state';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { FeatureProperties, KeyValueDistricts } from '../../../models/geo-data';
import { DataLayer } from '../../../models/data-layer';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { SearchInputComponent } from '../../atoms/search-input/search-input.component';
import { filterFeaturesBySearchWord } from '../../../stores/geo/geo-utils';
import { CheckAllComponent } from '../../atoms/check-all/check-all.component';
import { ComparisonValueStore } from '../../../stores/comparison-value/comparison-value.state';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { DividerModule } from 'primeng/divider';
import { DebugComponent } from '../../atoms/debug/debug.component';

@Component({
  selector: 'app-feature-checkbox-group',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CheckboxModule,
    DropdownModule,
    SearchInputComponent,
    CheckAllComponent,
    FormsModule,
    ScrollPanelModule,
    DividerModule,
    DebugComponent,
  ],
  templateUrl: './feature-checkbox-group.component.html',
  styleUrl: './feature-checkbox-group.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FeatureCheckboxGroupComponent),
      multi: true,
    },
  ],
})
export class FeatureCheckboxGroupComponent
  implements OnInit, OnChanges, ControlValueAccessor
{
  readonly geoDataStore = inject(GeoDataStore);
  readonly layerStore = inject(DataLayerStore);
  readonly comparisonValueStore = inject(ComparisonValueStore);

  @Input() label?: string;
  @Input() name = 'features';
  @Input() selection: string[] = [];
  @Input() dataLayer: DataLayer | null = null;
  @Input() dataLayerId = '';
  @Input() isStandalone = false;
  @Output() selectionChange = new EventEmitter<string[]>();

  districts: FeatureProperties[] | null = null;
  kvDistricts: KeyValueDistricts | null = null;
  touched = false;

  constructor() {
    effect(() => {
      const geoDataState = getState(this.geoDataStore);
      const layerState = getState(this.layerStore);
      this.setDistricts();
    });
  }

  /**
   * Determines if the specified key is not included in the current selection.
   *
   * @param {string} key - The key to check against the current selection.
   * @return {boolean} Returns true if the key is not in the selection, otherwise false.
   */
  isInverseSelected(key: string): boolean {
    // Returns true if the key is NOT in the selection
    return !this.selection.includes(key);
  }

  /**
   * Toggles the selection state of a given key based on its checked status.
   *
   * @param {string} key - The key representing the item to toggle selection for.
   * @param {boolean} isChecked - Represents whether the item is currently checked (true if selected, false if not).
   * @return {void} This method does not return any value.
   */
  toggleSelection(key: string, isChecked: boolean): void {
    if (isChecked) {
      // Remove from selection because the checkbox should act as "deselected"
      this.selection = this.selection.filter(item => item !== key);
    } else {
      // Add to selection because the checkbox is "selected"
      this.selection = [...this.selection, key];
    }

    // Notify about the change if necessary
    this.onChange(this.selection);
  }

  /**
   * Retrieves an array of keys from districts and comparisonValueStore.
   * Combines the keys extracted from the districts into a string format
   * and the keys from comparisonValueStore into a single array.
   *
   * @return {string[]} An array of keys from districts and comparisonValueStore. Returns an empty array if no keys are available.
   */
  getKeys(): string[] {
    return (
      this.districts
        ?.map(district => String(district.key))
        .concat(this.comparisonValueStore.keys()) ?? []
    );
  }

  /**
   * Handles changes to component input properties. This method is triggered whenever Angular detects changes to any bound input property.
   *
   * @param {SimpleChanges} changes - The object containing the updated values of the input properties. Each key in the object represents a property name, and the associated value contains details of the property change.
   * @return {void} This method does not return a value.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['dataLayerId']) {
      this.setDataLayer();
    }
  }

  /**
   * ngOnInit lifecycle hook that initializes component logic.
   *
   * This method checks if the dataLayer is available. If it is not,
   * it invokes the setDataLayer method. It also sets the label property
   * based on the conditions of the dataLayer's type and the existing value of label.
   *
   * @return {void} Does not return a value.
   */
  ngOnInit(): void {
    if (!this.dataLayer) {
      this.setDataLayer();
    }

    if (this.dataLayer && this.label !== undefined) {
      this.label =
        this.label ||
        (this.dataLayer && this.dataLayer.type !== 'Point'
          ? 'Teilräume'
          : 'Standorte') ||
        'Teilräume/Standorte';
    }
  }

  /**
   * Sets the data layer by retrieving it from the layer store using the associated dataLayerId.
   * If no matching layer is found, the data layer is set to null.
   *
   * @return {void} Does not return a value.
   */
  setDataLayer(): void {
    this.dataLayer = this.layerStore.getById(this.dataLayerId) || null;
  }

  /**
   * Updates the districts based on the provided searchWord and existing geoData.
   * Processes and filters the districts data from the data layer and updates
   * the internal state with the relevant districts.
   *
   * @param {string} [searchWord] Optional parameter to filter districts. If provided,
   * filters the districts matching the searchWord.
   * @return {void} Does not return a value.
   */
  setDistricts(searchWord?: string): void {
    if (this.dataLayer && this.dataLayer.geoData) {
      this.kvDistricts = this.geoDataStore.getDistrictsById(
        this.dataLayer.geoData
      );

      this.districts = this.kvDistricts
        ? Object.values(
            searchWord
              ? this.filterDistricts(this.kvDistricts, searchWord)
              : this.kvDistricts
          ).concat(this.comparisonValueStore.asArray())
        : [];
    }
  }

  /**
   * Filters the given districts based on the provided search word.
   *
   * @param {KeyValueDistricts} features - An object containing district information with key-value pairs.
   * @param {string} searchWord - The search term used to filter the districts.
   * @return {KeyValueDistricts} Filtered district data matching the search word.
   */
  filterDistricts(
    features: KeyValueDistricts,
    searchWord: string
  ): KeyValueDistricts {
    return filterFeaturesBySearchWord(features, searchWord);
  }

  /**
   * Handles changes to a selection and emits the updated value after a brief delay.
   *
   * @param {string[]} value - The updated selection of string items.
   */
  protected onChange = (value: string[]) => {
    setTimeout(() => {
      this.selectionChange.emit(value);
    }, 10);
  };

  private onTouched = () => {
    this.touched = false;
  };

  writeValue(value: any): void {
    this.selection = value || [];
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
}
